import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API, formatBRLc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QrCode, ArrowRight, ArrowLeft, Palette, Image as ImageIcon, Type, FileText, CreditCard, Printer, Check } from "lucide-react";

const MATERIALS = [
  {
    format: "a4", label: "Cartaz A4", icon: FileText, portrait: true,
    use: "Cole no vidro do carro",
    desc: "O clássico VENDE-SE em folha A4, com o QR Code em destaque. Máximo impacto pra quem passa na rua e no estacionamento.",
  },
  {
    format: "postal", label: "Cartão Postal", icon: ImageIcon, portrait: false,
    use: "Com foto do carro",
    desc: "Formato 14,8 × 10,5 cm com a foto do carro, preço e QR. Deixe no para-brisa ou entregue a quem demonstrar interesse.",
  },
  {
    format: "card", label: "Cartão de Visita", icon: CreditCard, portrait: false,
    use: "Entregue na mão",
    desc: "Formato de bolso (8,5 × 5,4 cm) com foto, preço e QR. Imprima vários por folha e distribua onde quiser.",
  },
];

const EXTRAS = [
  { icon: Palette, t: "3 cores", d: "Vermelho, preto ou branco." },
  { icon: ImageIcon, t: "Sua foto", d: "Escolha qual foto do anúncio aparece." },
  { icon: Type, t: "Texto do destaque", d: "VENDE-SE, ABAIXO DA FIPE, ACEITO TROCA ou o seu." },
  { icon: Printer, t: "PNG e PDF", d: "Baixe pronto ou imprima vários por folha." },
];

export default function Materiais() {
  const navigate = useNavigate();

  return (
    <div data-testid="materiais-page">
      {/* HERO */}
      <section className="bg-obsidian text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-white/50 hover:text-white mb-6" data-testid="materiais-back">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <span className="inline-flex items-center gap-2 bg-blaze/15 text-blaze text-xs font-800 px-3 py-1.5 rounded-full uppercase tracking-wide">
            <QrCode className="w-4 h-4" /> Kit de Divulgação com QR
          </span>
          <h1 className="mt-5 font-heading font-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            O que você recebe<br /><span className="text-blaze">ao assinar o QR.</span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">
            Por {formatBRLc(29)} a cada 3 meses, você libera o QR Code exclusivo do PicCar e baixa <b className="text-white">3 materiais prontos</b> pra divulgar o carro na rua. Todos levam quem escaneia direto pro seu anúncio — com fotos, preço e chat seguro.
          </p>
        </div>
      </section>

      {/* 3 MATERIAIS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {MATERIALS.map((m, i) => (
            <motion.div key={m.format} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }} className="flex flex-col" data-testid={`material-${m.format}`}>
              <div className="relative bg-secondary/40 border border-border rounded-sm p-5 flex items-center justify-center overflow-hidden" style={{ minHeight: 300 }}>
                <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 bg-obsidian text-white text-[11px] font-700 px-2 py-1 rounded-sm">
                  <m.icon className="w-3 h-3 text-blaze" /> {m.label}
                </span>
                <img
                  src={`${API}/demo/material.png?format=${m.format}`}
                  alt={`Exemplo de ${m.label} PicCar`}
                  data-testid={`material-img-${m.format}`}
                  className={`w-full h-auto object-contain shadow-xl rounded-sm ${m.portrait ? "max-h-[440px]" : "max-h-[280px]"}`}
                />
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-800 text-xl text-obsidian">{m.label}</h3>
                  <span className="text-xs font-700 text-blaze bg-blaze/10 px-2 py-0.5 rounded-sm">{m.use}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PERSONALIZAÇÃO */}
      <section className="bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <div className="text-center max-w-xl mx-auto mb-10">
            <p className="overline text-blaze mb-2">Tudo personalizável</p>
            <h2 className="font-heading font-900 text-3xl text-obsidian">Do seu jeito, com o seu QR</h2>
            <p className="mt-2 text-muted-foreground">Cada material sai com o QR Code exclusivo do seu anúncio — e você ajusta os detalhes antes de imprimir.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {EXTRAS.map((e, i) => (
              <div key={i} className="bg-secondary/40 border border-border rounded-sm p-5" data-testid={`extra-${i}`}>
                <div className="w-11 h-11 rounded-sm bg-obsidian flex items-center justify-center">
                  <e.icon className="w-5 h-5 text-blaze" strokeWidth={1.6} />
                </div>
                <p className="mt-4 font-heading font-800 text-base text-obsidian">{e.t}</p>
                <p className="mt-1 text-sm text-muted-foreground">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="bg-obsidian rounded-sm p-8 sm:p-12 text-white flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-900 text-3xl md:text-4xl">Anuncie grátis e assine o QR quando quiser</h2>
            <p className="mt-2 text-white/60 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-blaze" /> Anúncio 100% grátis</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-blaze" /> Kit por {formatBRLc(29)}/trimestre</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-blaze" /> Cancele quando quiser</span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button className="h-12 px-8 bg-blaze hover:bg-white hover:text-obsidian text-white font-700 rounded-sm" onClick={() => navigate("/anunciar")} data-testid="materiais-anunciar-btn">
              Anunciar meu carro <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/precos" className="h-12 px-8 inline-flex items-center justify-center border border-white/20 hover:border-white text-white font-700 rounded-sm transition-colors" data-testid="materiais-precos-link">
              Ver preços
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
